import type { Competitor } from '../types';

// Researched 2026-11-03. Every claim links to the page it came from.
// To correct something: change it here, update sourceUrl and lastReviewed.

export const metabase: Competitor = {
  "id": "metabase",
  "name": "Metabase",
  "group": "bi",
  "website": "https://www.metabase.com",
  "owner": "Metabase, Inc. (independent; open-source core)",
  "summary": "Open-source BI tool with a visual query builder, SQL editor, dashboards and embedding. It is available free to self-host or as paid cloud and self-hosted plans, and suits startups and product teams that want low-cost self-service reporting.",
  "bestFor": [
    "Teams that want to self-host for free or keep BI inside their own infrastructure",
    "SaaS companies embedding customer-facing dashboards on a budget, since guest embeds work in the free edition",
    "Small companies that want simple self-service charts and dashboards with transparent pricing"
  ],
  "notFor": [
    "Finance or ops users who expect a spreadsheet grid with cell formulas on warehouse data",
    "Teams that need governed writeback to Snowflake (editable tables support only PostgreSQL and MySQL)",
    "Enterprises that need deep code-based modeling (Data Studio is UI-driven)"
  ],
  "strengths": [
    "Open source and self-hostable, with a free edition that has unlimited users. Sigma is SaaS-only.",
    "Published pricing: Starter from $100/month including 5 users, with low per-user add-on rates.",
    "AI is free in the open-source edition: since 2026, Metabot works with your own LLM key (Anthropic, OpenAI, Bedrock, Azure or a self-hosted model) and there is an official MCP server.",
    "Guest (JWT-signed) embedding is free in the open-source edition, which lowers the cost of basic customer-facing analytics.",
    "Remote sync stores the Library and transforms in GitHub, GitLab or Bitbucket for a pull-request workflow between dev and prod instances (Pro/Enterprise)."
  ],
  "limitations": [
    "No spreadsheet-style grid. Analysis uses a notebook-style query builder or SQL.",
    "Editable tables (writeback) need Pro or Enterprise and support only PostgreSQL and MySQL.",
    "Row and column security, remote sync and SSO need Pro or Enterprise.",
    "Python is limited to batch transforms through a paid add-on: pandas only, in-memory, and one Python transform at a time.",
    "Exports carry Metabase branding unless you are on Pro or Enterprise."
  ],
  "pricing": {
    "model": "flat base fee including some users, plus per-user overage; usage-based add-ons",
    "publicStartingPrice": "Starter $100/month (or $1,080/year) including 5 users, then $6/user/month; Pro $575/month including 10 users, then $12/user/month",
    "freeTier": "yes: Open Source edition, self-hosted, unlimited users",
    "notes": "Enterprise starts at $20,000/year. Every signed-in user counts, including embedded users. Add-ons are billed on usage: AI ($3.75 per 1M tokens, or bring your own key), transforms (1,000 runs included, then about $0.01 per run) and storage (from $2 per 1M rows). Metabase added the Storage and Advanced Transforms add-ons in Q1 2026.",
    "sourceUrl": "https://www.metabase.com/pricing",
    "checkedOn": "2026-11-03"
  },
  "features": {
    "live_warehouse_query": {
      "value": "yes",
      "note": "Queries the database live by default, with optional result caching.",
      "sourceUrl": "https://metabase.com/learn/metabase-basics/administration/administration-and-operation/metabase-and-your-db"
    },
    "spreadsheet_interface": {
      "value": "no",
      "note": "Visual query builder and SQL only; no cell-formula grid.",
      "sourceUrl": "https://www.metabase.com/lp/metabase-vs-omni"
    },
    "sql_editor": {
      "value": "yes",
      "note": "Native SQL questions and SQL snippets, which can be versioned in the Library.",
      "sourceUrl": "https://www.metabase.com/docs/latest/data-modeling/semantic-layer/remote-sync"
    },
    "python_support": {
      "value": "partial",
      "note": "Python transforms only: needs the Advanced transforms add-on, pandas only, writes tables back to the database. No interactive notebook.",
      "sourceUrl": "https://www.metabase.com/docs/latest/data-modeling/transforms/python",
      "badge": "Paid add-on"
    },
    "writeback": {
      "value": "partial",
      "note": "Editable tables (Pro/Enterprise) support PostgreSQL and MySQL only. Actions offer forms and buttons.",
      "sourceUrl": "https://www.metabase.com/docs/latest/data-modeling/editable-tables",
      "badge": "Postgres & MySQL only"
    },
    "semantic_layer": {
      "value": "yes",
      "note": "Data Studio (v59, Mar 2026) adds a Library of metrics, segments and measures, plus a glossary and dependency graph. UI-based.",
      "sourceUrl": "https://metabase.com/blog/meet-data-studio-semantic-layer"
    },
    "embedded_analytics": {
      "value": "yes",
      "note": "Modular embedding with Guest (free) or SSO, a React SDK, and full-app embedding. Deep theming and multi-tenant features need Pro.",
      "sourceUrl": "https://www.metabase.com/blog/we-simplified-metabase-embedding"
    },
    "ai_assistant": {
      "value": "yes",
      "note": "Metabot, GA since v58 (Jan 2026). Bring-your-own LLM and an MCP server; SQL generation is included in open source.",
      "sourceUrl": "https://www.metabase.com/releases-ai"
    },
    "version_control_git": {
      "value": "yes",
      "note": "Remote sync with GitHub, GitLab or Bitbucket, Pro/Enterprise only. Covers the Library and transforms, not permissions or table metadata.",
      "sourceUrl": "https://www.metabase.com/docs/latest/data-modeling/semantic-layer/remote-sync",
      "badge": "Pro plan and up"
    },
    "open_source_or_self_host": {
      "value": "yes",
      "note": "Free open-source edition. Paid plans can also be self-hosted.",
      "sourceUrl": "https://www.metabase.com/pricing"
    },
    "desktop_authoring_required": {
      "value": "no",
      "note": "Web application.",
      "sourceUrl": "https://metabase.com/learn/metabase-basics/administration/administration-and-operation/metabase-and-your-db"
    },
    "scheduled_delivery": {
      "value": "yes",
      "note": "Dashboard subscriptions and alerts by email or Slack, with CSV/XLSX or PDF attachments.",
      "sourceUrl": "https://www.metabase.com/docs/latest/dashboards/subscriptions"
    },
    "row_level_security": {
      "value": "yes",
      "note": "Row and column security by user attribute; Pro/Enterprise only.",
      "sourceUrl": "https://metabase.com/docs/latest/permissions/row-and-column-security"
    },
    "excel_export": {
      "value": "yes",
      "note": "CSV, XLSX, JSON and PNG for questions; PDF for dashboards. Exports are branded on lower plans.",
      "sourceUrl": "https://www.metabase.com/docs/latest/questions/exporting-results"
    }
  },
  "recentChanges": [
    {
      "date": "2026-01",
      "change": "v58: Metabot out of beta. Embedding consolidated into Modular (Guest/SSO) and Full-app; static and interactive embedding were renamed.",
      "sourceUrl": "https://www.metabase.com/blog/we-simplified-metabase-embedding"
    },
    {
      "date": "2026-02",
      "change": "New usage-based add-ons for storage and Advanced Transforms (SQL + Python), per third-party pricing tracker.",
      "sourceUrl": "https://pricingsaas.com/companies/metabase/diffs/2026Q1"
    },
    {
      "date": "2026-03",
      "change": "v59: Data Studio semantic layer (Library, glossary, dependency graph, transforms). AI SQL generation added to open source.",
      "sourceUrl": "https://metabase.com/blog/meet-data-studio-semantic-layer"
    },
    {
      "date": "2026-04",
      "change": "v60: AI tools open-sourced, official MCP server, bring-your-own Anthropic key, Metabot in Slack.",
      "sourceUrl": "https://www.metabase.com/releases-ai"
    },
    {
      "date": "2026-07",
      "change": "v63: Metabot supports OpenAI, Bedrock and Azure; CSV uploads to Snowflake; PDF subscription attachments.",
      "sourceUrl": "https://www.metabase.com/releases/metabase-63"
    },
    {
      "date": "2026-10",
      "change": "v64-65: bigger MCP server, self-hosted open models via vLLM, and data apps in beta.",
      "sourceUrl": "https://www.metabase.com/releases-ai"
    }
  ],
  "sources": [
    {
      "title": "Metabase pricing",
      "url": "https://www.metabase.com/pricing"
    },
    {
      "title": "Metabase AI releases",
      "url": "https://www.metabase.com/releases-ai"
    },
    {
      "title": "Metabase 63 release",
      "url": "https://www.metabase.com/releases/metabase-63"
    },
    {
      "title": "Meet Data Studio",
      "url": "https://metabase.com/blog/meet-data-studio-semantic-layer"
    },
    {
      "title": "We simplified Metabase embedding",
      "url": "https://www.metabase.com/blog/we-simplified-metabase-embedding"
    },
    {
      "title": "Metabase docs: Remote sync",
      "url": "https://www.metabase.com/docs/latest/data-modeling/semantic-layer/remote-sync"
    },
    {
      "title": "Metabase docs: Editable tables",
      "url": "https://www.metabase.com/docs/latest/data-modeling/editable-tables"
    },
    {
      "title": "Metabase docs: Python transforms",
      "url": "https://www.metabase.com/docs/latest/data-modeling/transforms/python"
    },
    {
      "title": "Metabase docs: Row and column security",
      "url": "https://metabase.com/docs/latest/permissions/row-and-column-security"
    },
    {
      "title": "Metabase docs: Exporting results",
      "url": "https://www.metabase.com/docs/latest/questions/exporting-results"
    },
    {
      "title": "Metabase docs: Dashboard subscriptions",
      "url": "https://www.metabase.com/docs/latest/dashboards/subscriptions"
    },
    {
      "title": "How Metabase works with your database",
      "url": "https://metabase.com/learn/metabase-basics/administration/administration-and-operation/metabase-and-your-db"
    },
    {
      "title": "PricingSaaS: Metabase 2026 Q1 changes (third party)",
      "url": "https://pricingsaas.com/companies/metabase/diffs/2026Q1"
    }
  ],
  "lastReviewed": "2026-11-03"
};
